import {
  DEFAULT_MARKDOWN_PLACEHOLDER,
  DEFAULT_COLOR_PRIMARY,
} from '../constants'
import { basePropertiesSchema } from '../base-schema-properties'
import { getButtonPropertiesWithAuth, textFontFamilySchema } from '../common'

const responsiveProperties = (
  prefix: 'mobile' | 'medium',
  viewName: string,
  toggleTitle: string
) => {
  const enabledProperty = `override${
    prefix.charAt(0).toUpperCase() + prefix.slice(1)
  }View`
  const hidden = `{{ parentFormData.${enabledProperty} !== true }}`
  const bgImageProperty = `${prefix}BgImage`

  return {
    [enabledProperty]: {
      title: toggleTitle,
      type: 'boolean',
      default: false,
    },
    [`${prefix}BgImage`]: {
      title: `Background image — ${viewName}`,
      description: 'Leave empty to use the desktop background.',
      type: 'string',
      'ui:hidden': hidden,
    },
    [`${prefix}BgColor`]: {
      title: `Background color — ${viewName}`,
      description:
        'Choose a color to replace the desktop background image for this view.',
      type: 'string',
      'ui:hidden': `{{ parentFormData.${enabledProperty} !== true || !!parentFormData.${bgImageProperty} }}`,
    },
    [`${prefix}ImageRatioMode`]: {
      title: `Use image ratio — ${viewName}`,
      type: 'string',
      enum: ['desktop', 'enabled', 'disabled'],
      enumNames: [
        'Same as desktop',
        'Use image ratio',
        "Don't use image ratio",
      ],
      default: 'desktop',
      'ui:widget': 'RadioWidget',
      'ui:hidden': hidden,
    },
    [`${prefix}PaddingSize`]: {
      title: `Paddings — ${viewName}`,
      enum: ['xs', 'sm', 'md', 'lg'],
      enumNames: ['Extra small', 'Small', 'Medium', 'Large'],
      default: 'md',
      'ui:widget': 'RadioWidget',
      'ui:hidden': `{{ parentFormData.${enabledProperty} !== true || parentFormData.useImageRatio !== true || parentFormData.${prefix}ImageRatioMode !== "disabled" }}`,
    },
    [`${prefix}LogoWidth`]: {
      title: `Logo width — ${viewName} (in px)`,
      description: 'Uses the desktop width until changed.',
      type: 'number',
      'ui:hidden': hidden,
    },
    [`${prefix}LogoAlign`]: {
      title: `Logo horizontal alignment — ${viewName}`,
      description: 'Uses the desktop alignment until changed.',
      enum: ['left', 'center', 'right'],
      enumNames: ['Left', 'Center', 'Right'],
      'ui:widget': 'RadioWidget',
      'ui:hidden': hidden,
    },
    [`${prefix}MaxWidth`]: {
      title: `Max width of the content — ${viewName} (in px)`,
      description: 'Uses the desktop width until changed.',
      type: 'number',
      'ui:hidden': hidden,
    },
    [`${prefix}SloganMaxWidth`]: {
      title: `Slogan max width — ${viewName} (in px)`,
      description: 'Uses the desktop slogan width until changed.',
      type: 'number',
      'ui:hidden': hidden,
    },
    [`${prefix}SloganTopMargin`]: {
      title: `Slogan top margin — ${viewName} (in px)`,
      description: 'Uses the desktop top margin until changed.',
      type: 'number',
      minimum: 0,
      'ui:hidden': hidden,
    },
    [`${prefix}SloganTextAlign`]: {
      title: `Slogan alignment — ${viewName}`,
      description: 'Uses the desktop slogan alignment until changed.',
      enum: ['left', 'center', 'right'],
      enumNames: ['Left', 'Center', 'Right'],
      'ui:widget': 'RadioWidget',
      'ui:hidden': hidden,
    },
    [`${prefix}SloganFontSize`]: {
      title: `Slogan font size — ${viewName} (in px)`,
      description: 'Uses the desktop size until changed.',
      type: 'number',
      minimum: 1,
      'ui:hidden': hidden,
    },
    [`${prefix}SloganLineHeight`]: {
      title: `Slogan line height — ${viewName} (relative to font size)`,
      description: 'Uses the desktop line height until changed.',
      type: 'number',
      minimum: 0.1,
      multipleOf: 0.01,
      'ui:hidden': hidden,
    },
    [`${prefix}TextFontSize`]: {
      title: `Text font size — ${viewName} (in px)`,
      description: 'Uses the desktop size until changed.',
      type: 'number',
      minimum: 1,
      'ui:hidden': hidden,
    },
    [`${prefix}TextLineHeight`]: {
      title: `Text line height — ${viewName} (relative to font size)`,
      description: 'Uses the desktop line height until changed.',
      type: 'number',
      minimum: 0.1,
      multipleOf: 0.01,
      'ui:hidden': hidden,
    },
    [`${prefix}TextAlign`]: {
      title: `Text Align — ${viewName}`,
      description: 'Uses the desktop alignment until changed.',
      enum: ['left', 'center', 'right'],
      enumNames: ['Left', 'Center', 'Right'],
      'ui:widget': 'RadioWidget',
      'ui:hidden': hidden,
    },
    [`${prefix}TextButtonsGap`]: {
      title: `Space between text and buttons — ${viewName} (in px)`,
      description: 'Uses the desktop spacing until changed.',
      type: 'number',
      minimum: 0,
      'ui:hidden': hidden,
    },
    [`${prefix}PreserveAuthButtonsSpaceMode`]: {
      title: `Preserve hidden auth buttons space — ${viewName}`,
      type: 'string',
      enum: ['desktop', 'enabled', 'disabled'],
      enumNames: ['Same as desktop', 'Preserve space', 'Collapse space'],
      default: 'desktop',
      'ui:widget': 'RadioWidget',
      'ui:hidden': hidden,
    },
    [`${prefix}CenterTextWhenAuthButtonsHiddenMode`]: {
      title: `Center text when auth buttons are hidden — ${viewName}`,
      type: 'string',
      enum: ['desktop', 'enabled', 'disabled'],
      enumNames: ['Same as desktop', 'Center text', "Don't center text"],
      default: 'desktop',
      'ui:widget': 'RadioWidget',
      'ui:hidden': hidden,
    },
  }
}

const heroButtonProperties = getButtonPropertiesWithAuth()

export default {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Hero',
  description: 'Hero block for the home page. Use Cover on all other pages.',
  type: 'object',
  required: [],
  properties: {
    ...basePropertiesSchema,
    logo: {
      title: 'Logo or other svg/png image before text',
      type: 'string',
      default: '',
    },
    logoWidth: {
      title: 'Logo width (in px)',
      description: "If it's not set, the logo width will be 240px",
      type: 'number',
    },
    logoAlign: {
      title: 'Logo horizontal alignment',
      enum: ['left', 'center', 'right'],
      enumNames: ['Left', 'Center', 'Right'],
      default: 'left',
      'ui:widget': 'RadioWidget',
    },
    slogan: {
      title: 'Slogan (safe HTML)',
      description:
        'Allowed tags: span, strong, b, em, i, br. Any CSS declarations are allowed in the span style attribute.',
      type: 'string',
      'ui:options': {
        placeholder:
          'Your <span style="color: #ff6600; font-size: 48px; font-weight: 300">styled slogan</span>',
        type: 'textarea',
        rows: 8,
      },
    },
    sloganTextAlign: {
      title: 'Slogan alignment',
      enum: ['left', 'center', 'right'],
      enumNames: ['Left', 'Center', 'Right'],
      default: 'center',
      'ui:widget': 'RadioWidget',
    },
    sloganFontSize: {
      title: 'Slogan font size (in px)',
      type: 'number',
      default: 30,
      minimum: 1,
    },
    sloganLineHeight: {
      title: 'Slogan line height (relative to font size)',
      description: 'For example, 1.2 means 120% of the font size.',
      type: 'number',
      minimum: 0.1,
      multipleOf: 0.01,
    },
    sloganFontFamily: {
      title: 'Slogan font',
      type: 'string',
      enum: ['Montserrat', 'Unbounded', 'system', 'custom'],
      enumNames: ['Montserrat', 'Unbounded', 'System', 'Custom Google Font'],
      default: 'Montserrat',
    },
    sloganCustomFont: {
      title: 'Custom Google Font family name',
      description: 'For example: Roboto Condensed',
      type: 'string',
      'ui:hidden': '{{ parentFormData.sloganFontFamily !== "custom" }}',
    },
    sloganCustomFontLight: {
      title: 'Load Light (300)',
      type: 'boolean',
      default: false,
      'ui:hidden': '{{ parentFormData.sloganFontFamily !== "custom" }}',
    },
    sloganCustomFontRegular: {
      title: 'Load Regular (400)',
      type: 'boolean',
      default: true,
      'ui:hidden': '{{ parentFormData.sloganFontFamily !== "custom" }}',
    },
    sloganCustomFontBold: {
      title: 'Load Bold (700)',
      type: 'boolean',
      default: false,
      'ui:hidden': '{{ parentFormData.sloganFontFamily !== "custom" }}',
    },
    sloganCustomFontLightItalic: {
      title: 'Load Light Italic (300)',
      type: 'boolean',
      default: false,
      'ui:hidden': '{{ parentFormData.sloganFontFamily !== "custom" }}',
    },
    sloganCustomFontRegularItalic: {
      title: 'Load Regular Italic (400)',
      type: 'boolean',
      default: false,
      'ui:hidden': '{{ parentFormData.sloganFontFamily !== "custom" }}',
    },
    sloganCustomFontBoldItalic: {
      title: 'Load Bold Italic (700)',
      type: 'boolean',
      default: false,
      'ui:hidden': '{{ parentFormData.sloganFontFamily !== "custom" }}',
    },
    sloganMaxWidth: {
      title: 'Slogan max width (in px)',
      description: "If it's not set, the slogan width will be 576px",
      type: 'number',
    },
    sloganTopMargin: {
      title: 'Slogan top margin (in px)',
      type: 'number',
      minimum: 0,
    },
    sloganTextGap: {
      title: 'Space between slogan and text (in px)',
      type: 'number',
      default: 32,
      minimum: 0,
    },
    text: {
      title: 'Text',
      type: 'string',
      'ui:options': {
        placeholder: DEFAULT_MARKDOWN_PLACEHOLDER,
        type: 'textarea',
        rows: 6,
      },
    },
    textColor: {
      title: 'Text Color',
      type: 'string',
      default: '#ffffff',
    },
    ...textFontFamilySchema,
    textFontSize: {
      title: 'Text font size (in px)',
      type: 'number',
      default: 20,
      minimum: 1,
    },
    textLineHeight: {
      title: 'Text line height (relative to font size)',
      description: 'For example, 1.4 means 140% of the font size.',
      type: 'number',
      minimum: 0.1,
      multipleOf: 0.01,
    },
    linkColor: {
      title: 'Link Color',
      type: 'string',
      default: DEFAULT_COLOR_PRIMARY,
    },
    textAlign: {
      title: 'Text Align',
      enum: ['left', 'center', 'right'],
      enumNames: ['Left', 'Center', 'Right'],
      default: 'center',
      'ui:widget': 'RadioWidget',
    },
    textButtonsGap: {
      title: 'Space between text and buttons (in px)',
      description: 'Uses the default responsive spacing until changed.',
      type: 'number',
      minimum: 0,
    },
    preserveAuthButtonsSpace: {
      title: 'Preserve space when auth buttons are hidden',
      description:
        'Keeps the buttons container height when all configured auth buttons are hidden for an authorized user.',
      type: 'boolean',
      default: true,
    },
    centerTextWhenAuthButtonsHidden: {
      title: 'Center text when auth buttons are hidden',
      description:
        'Adds equal spacing above and below the text. Can be combined with preserving the hidden buttons space.',
      type: 'boolean',
      default: false,
    },
    useImageRatio: {
      title: 'Use image ratio',
      type: 'boolean',
      default: false,
      'ui:style':
        '{{ parentFormData.bgImage ? {} : { "pointer-events": "none", opacity: "20%" } }}',
    },
    bgImage: {
      title: 'Background image',
      type: 'string',
      default: '',
    },
    bgColor: {
      title: 'Background color',
      type: 'string',
      'ui:hidden': '{{ !!parentFormData.bgImage }}',
    },
    paddingSize: {
      title: 'Paddings',
      enum: ['xs', 'sm', 'md', 'lg'],
      enumNames: ['Extra small', 'Small', 'Medium', 'Large'],
      default: 'md',
      'ui:widget': 'RadioWidget',
      'ui:hidden':
        '{{ parentFormData.useImageRatio === true && parentFormData.bgImage }}',
    },
    maxWidth: {
      title: 'Max width of the content (in px)',
      description: "If it's not set, the content width will be 576px",
      type: 'number',
    },
    ...responsiveProperties('mobile', 'mobile', 'Override mobile view'),
    ...responsiveProperties(
      'medium',
      'tablets and small laptops',
      'Override tablet and small laptop view'
    ),
    buttons: {
      type: 'array',
      title: 'A list of buttons',
      'ui:options': {
        showIndexNumber: true,
      },
      items: {
        type: 'object',
        properties: heroButtonProperties,
      },
    },
  },
}
